import { useState } from "react";
import { Link } from "react-router-dom";
import { FiMail, FiSend, FiUser } from "react-icons/fi";
import { motion } from "framer-motion";
import api from "../services/api";

const ForgotPassword = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setIsSubmitting(true);

    try {
      const response = await api.post("/forgot-password", { username, email });
      if (response.data?.status !== "success") {
        setErrorMessage(response.data?.message || "Failed to send reset link.");
        return;
      }

      setSuccessMessage(
        response.data?.message || "Reset link sent. Please check your email."
      );
      setUsername("");
      setEmail("");
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to send reset link.";
      if (msg && typeof msg === "object") {
        setErrorMessage(Object.values(msg).flat().join(" "));
      } else {
        setErrorMessage(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-[linear-gradient(135deg,#e0f2fe_0%,#fce7f3_55%,#fed7aa_100%)] px-6 py-10"
    >
      <div className="mx-auto flex w-full max-w-[900px] flex-col gap-6">
        <header className="flex items-center gap-3 rounded-3xl border border-white/60 bg-white/90 px-6 py-4 shadow-lg shadow-slate-200/60">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--brand-blue)_0%,var(--brand-blue-2)_100%)] text-white">
            <FiMail />
          </div>
          <div className="text-lg font-semibold text-slate-900">
            RMC2 Emergency Care (ER)
          </div>
          <Link
            to="/login"
            className="ml-auto text-sm font-semibold text-[var(--brand-blue)] hover:text-[var(--brand-blue-2)]"
          >
            Back to Login
          </Link>
        </header>

        <div className="rounded-3xl border border-white/60 bg-white/90 p-8 shadow-lg shadow-slate-200/60">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-[var(--brand-blue)]">
              <FiSend />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                Reset
              </p>
              <h2 className="text-2xl font-semibold text-slate-900">
                Forgot Password
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Enter your username and email to receive a password reset link.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div>
              <label className="text-sm font-semibold text-slate-600">
                Username
              </label>
              <div className="relative mt-2">
                <FiUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="Your username"
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">
                Email Address
              </label>
              <div className="relative mt-2">
                <FiMail className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@email.com"
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>

            {errorMessage && (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {errorMessage}
              </div>
            )}
            {successMessage && (
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                {successMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-2xl bg-[var(--brand-orange)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Sending..." : "Send Reset Link"}
            </button>
          </form>
        </div>
      </div>
    </motion.div>
  );
};

export default ForgotPassword;
